import { useConfigurationStore } from "@/store/configuration.js"
import { useAppStateStore } from "@/store/appState.js"

import { defaultColors } from "@/constants/defaults.js"
import { DESKTOP_TOP_BAR_HEIGHT, MOBILE_TOP_BAR_HEIGHT, MOBILE_BOTTOM_BAR_BUTTONS_AREA_HEIGHT } from "@/constants/barHeights.js"

const getBarClasses = () => {
  const appStateStore = useAppStateStore()
  const barData = [
    { name: "desktop-top-bar", height: DESKTOP_TOP_BAR_HEIGHT },
    { name: "mobile-top-bar", height: MOBILE_TOP_BAR_HEIGHT },
    {
      name: "mobile-bottom-bar",
      height: appStateStore.mobileBottomBarHeight,
      padding: appStateStore.mobileBottomBarPadding
    },
    {
      name: "mobile-bottom-bar-buttons-area",
      height: MOBILE_BOTTOM_BAR_BUTTONS_AREA_HEIGHT
    }
  ]

  let barClasses = ""
  barData.forEach((d) => {
    if (d.height !== undefined) {
      barClasses += `--${d.name}-height: ${d.height}px;`
    }
    if (d.padding !== undefined) {
      barClasses += `--${d.name}-padding: ${d.padding}px;`
    }
  })

  return barClasses
}

const getColorsFromConfiguration = () => {
  const configurationStore = useConfigurationStore()
  const colors = preProcessColors(configurationStore.configuration.colors)

  return [
    {
      name: "post",
      colors: [
        {
          name: "text",
          value: colors.postText,
          variants: [5, 10, 30, 40, 60]
        },
        {
          name: "background",
          value: colors.postBackground,
          variants: []
        },
        {
          name: "tint",
          value: colors.postTint,
          variants: [10, 15, 25, 30]
        },
        {
          name: "detail",
          value: colors.postDetail,
          variants: [5, 10, 15, 20, 30, 40, 50]
        }
      ]
    },
    {
      name: "wall",
      colors: [
        {
          name: "text",
          value: colors.wallText,
          variants: [10, 20, 40, 60]
        },
        {
          name: "background",
          value: colors.wallBackground,
          variants: []
        },
        {
          name: "tint",
          value: colors.wallTint,
          variants: []
        },
        {
          name: "detail",
          value: colors.wallDetail,
          variants: [10, 15, 40, 50]
        }
      ]
    },
    {
      name: "top-bar",
      colors: [
        {
          name: "text",
          value: colors.topBarText,
          variants: [5, 40]
        },
        {
          name: "background",
          value: colors.topBarBackground,
          variants: []
        },
        {
          name: "tint",
          value: colors.topBarTint,
          variants: [5, 10]
        }
      ]
    }
  ]
}

// Set app styles by injecting <style> tag
export const setAppStyles = () => {
  const colorsArray = getColorsFromConfiguration()

  let rootStyles = ""
  let classesStyles = ""

  colorsArray.forEach((type) => {
    type.colors.forEach((color) => {
      rootStyles += `--${type.name}-${color.name}: ${rgbToRgbaString(hexToRgb(color.value), 1)};\n`
      color.variants.forEach((variant) => {
        rootStyles += `--${type.name}-${color.name}-${variant}: ${rgbToRgbaString(hexToRgb(color.value), variant / 100)};\n`
      })
    })
  })
  ;["color", "background", "border"].forEach((label) => {
    const cssName = label === "color" ? label : `${label}-color`
    colorsArray.forEach((type) => {
      type.colors.forEach((color) => {
        //Nested .v-overlay__content to override some styles that were previously overriden by #app, now overlay is outside of #app
        classesStyles += `#app .${label}-${type.name}-${color.name} { ${cssName}: var(--${type.name}-${color.name}) !important; }\n`
        classesStyles += `.v-overlay-container .v-overlay__content .${label}-${type.name}-${color.name} { ${cssName}: var(--${type.name}-${color.name}) !important; }\n`
        color.variants.forEach((variant) => {
          classesStyles += `#app .${label}-${type.name}-${color.name}-${variant} { ${cssName}: var(--${type.name}-${color.name}-${variant}) !important; }\n`
          classesStyles += `.v-overlay-container .v-overlay__content .${label}-${type.name}-${color.name}-${variant} { ${cssName}: var(--${type.name}-${color.name}-${variant}) !important; }\n`
        })
      })
    })
  })

  const styleTagId = "api-styles"

  const existingElement = document.getElementById(styleTagId)
  if (existingElement) {
    existingElement.parentNode.removeChild(existingElement)
  }

  const node = document.createElement("style")
  node.setAttribute("id", styleTagId)

  node.innerHTML = `
  :root {
    ${rootStyles}
    ${getBarClasses()}
  }

  ${classesStyles}
  `
  document.body.appendChild(node)
}

export const rgbToRgbaString = function (rgb, opacity) {
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity})`
}

export const hexToRgb = function (hex) {
  const defaultResult = {
    r: 0,
    g: 0,
    b: 0
  }

  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return match
    ? {
        r: parseInt(match[1], 16),
        g: parseInt(match[2], 16),
        b: parseInt(match[3], 16)
      }
    : defaultResult
}

const preProcessColors = function (colors) {
  if (!colors) {
    return defaultColors
  }

  Object.keys(defaultColors).forEach((c) => {
    if (!colors[c]) {
      colors[c] = defaultColors[c]
    }
  })

  return colors
}

export const remToPixels = (rem) => {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
}

export const extractStyleElementFromHTMLAndInjectToDocument = ({ html, tagId }) => {
  const existingElement = document.getElementById(tagId)
  if (existingElement) {
    existingElement.parentNode.removeChild(existingElement)
  }

  const doc = document.createElement("html")
  const htmlWithoutFontfaceDeclarations = html.replace(/@font-face{.*?}/g, "")
  doc.innerHTML = htmlWithoutFontfaceDeclarations

  const styleElement = doc.querySelector("style")

  if (!styleElement) {
    return
  }

  styleElement.setAttribute("id", tagId)

  // Prepend to head so these styles don't override other styles which have the same specificity
  document.head.prepend(styleElement)
}
